import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { AlertTriangle, ChevronLeft, Users } from "lucide-react";
import { Composer } from "../../pages/comms/Messages/Composer";
import { Toaster } from "../../common/Toaster/Toaster";
import { bulkSendMessage } from "../../service/comms/messagesService";
import { post as commsPost } from "../../service/comms/commsApi";
import { requestChatResync } from "../../hooks/comms/RoomsUnreadProvider";
import { RealtimeContext } from "../../hooks/comms/RealtimeProvider";
import { BulkProgressList } from "./BulkProgressList";
import {
  ROOM_FAILED,
  clearBulkSend,
  getActiveBatchId,
  getBulkSendSnapshot,
  settleBulkSend,
  startBulkSend,
  subscribeBulkSends,
} from "./bulkSendStore";

/**
 * How long to wait for the next progress event before calling a batch
 * unfinished-but-unknown.
 *
 * SCALED WITH THE BATCH, not flat. The server sends rooms sequentially and a
 * room carrying media takes seconds on its own, so a fixed 30s was routinely
 * shorter than the send it was timing: a thirty-recipient image batch settled
 * to `unknown` while it was still delivering, leaving the first room as the
 * only "Sent" and the rest reading "Check the thread" for messages that had
 * landed. That reads as "it only went to one person".
 *
 * Capped, because a window this generous is a fallback for silence, not a
 * progress bar — a batch that has genuinely stopped reporting should still
 * settle in bounded time.
 */
const PROGRESS_IDLE_BASE_MS = 30000;
const PROGRESS_IDLE_PER_ROOM_MS = 1000;
const PROGRESS_IDLE_MAX_MS = 180000;

/** @param {number} total - Rooms in the batch. @returns {number} ms. */
const idleTimeoutFor = (total) =>
  Math.min(
    PROGRESS_IDLE_MAX_MS,
    PROGRESS_IDLE_BASE_MS + PROGRESS_IDLE_PER_ROOM_MS * (Number(total) || 0)
  );

/** The server's cap (`bulk_send_service.MAX_BULK_ROOMS`). Mirrored, not guessed. */
export const BULK_MAX_RECIPIENTS = 200;

/** How many names to spell out before collapsing into "+N more". */
const NAMES_SHOWN = 3;

/** Client-side id, matching `useRoomMessages.genKey`. */
const genKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `k_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

/**
 * Compose one message for many clients.
 *
 * Reuses `Composer` rather than growing a second input: attachments, the
 * presign upload, the busy state and Enter-to-send are already solved there.
 * `modes={["client"]}` hides the internal-note toggle — the endpoint refuses
 * `coach_only` anyway, and offering a control that would be rejected is its own
 * kind of bug.
 *
 * ── Why sending is two steps ────────────────────────────────────────────────
 * Pressing send in the composer does not send; it stages the message and shows
 * a confirm bar naming the recipient count. A single keystroke (Enter) reaching
 * twelve clients with no second look is not a risk worth taking, and this is
 * the one surface in the dock where a slip is not recoverable.
 *
 * Staging rather than blocking is deliberate: `Composer` clears its draft once
 * `onSubmit` resolves, so the text moves into `staged` and the confirm bar
 * shows it back. Cancelling re-seeds the composer through `initialText` /
 * `initialTextNonce`, which exist for exactly this.
 *
 * @param {Object} props
 * @param {Array<{ clientId: string, roomId: string, name: string }>} props.recipients
 * @param {() => void} props.onBack - Return to the selection, intact.
 * @param {() => void} props.onDone - Finished with this send: return to the
 *   selection AND clear it. Distinct from `onBack`, which must leave the
 *   selection alone — backing out of the composer is not the same act as
 *   dismissing a completed send, and `Send another` depends on the recipients
 *   still being there.
 * @param {(failedClientIds: string[]) => void} props.onSent - Reports which
 *   recipients did NOT get it, so the caller can narrow the selection to them.
 */
export function BulkComposePane({ recipients, onBack, onDone, onSent }) {
  const { connected, connectionEpoch } = useContext(RealtimeContext);
  const [toast, setToast] = useState(null);
  const [staged, setStaged] = useState(null);
  // Only covers the POST that returns 202 — the sends outlive it.
  const [accepting, setAccepting] = useState(false);

  /**
   * The batch THIS PANE is showing.
   *
   * Owned here rather than read from the store on every render, which is the
   * bug this replaces. `getActiveBatchId()` returns the newest batch whether or
   * not it has finished, and a finished batch nobody dismissed then took the
   * composer away: mounting the pane put it straight back into the progress
   * view of a send that was already over, with no way to write the next one.
   *
   * Seeded once, on mount, so a send that is still running when the dock is
   * closed is picked back up when it reopens — that resume is what the store
   * exists for. After that this pane decides what it is watching, and `null`
   * means "show the composer".
   */
  const [watchingId, setWatchingId] = useState(getActiveBatchId);
  /**
   * Who the batch went to, frozen at send time.
   *
   * `recipients` is recomputed from a room index the dock refreshes on a timer,
   * and the parent narrows the selection to the failures the moment a batch
   * settles — so the live prop is the wrong thing to label a progress list
   * with. The list must keep describing the send that happened.
   */
  const [sentTo, setSentTo] = useState(null);

  const [restore, setRestore] = useState({ text: "", nonce: 0 });
  // Held across retries on purpose: re-sending with the SAME key is what makes
  // a partial failure recoverable without messaging anyone twice — the server's
  // uniqueness is (room_id, idempotency_key). Rotated only after a clean send.
  const attemptKeyRef = useRef(genKey());

  /** The list this pane is talking about: who it went to, else who it would. */
  const shown = sentTo ?? recipients;

  const summary = useMemo(() => {
    const names = shown.map((r) => r.name);
    if (names.length <= NAMES_SHOWN) return names.join(", ");
    return `${names.slice(0, NAMES_SHOWN).join(", ")} +${
      names.length - NAMES_SHOWN
    } more`;
  }, [shown]);

  /** Composer's submit — stages, never sends. */
  const handleStage = useCallback(async (payload) => {
    setStaged(payload);
  }, []);

  const cancelStaged = useCallback(() => {
    setRestore((prev) => ({
      text: staged?.content || "",
      nonce: prev.nonce + 1,
    }));
    setStaged(null);
  }, [staged]);

  /**
   * Hand the batch to the server and switch to watching it.
   *
   * The await here covers only the 202 — the sends have not happened yet. Every
   * recipient is seeded as `sending` before the request goes out, so the list
   * is populated the instant the pane switches rather than a round-trip later.
   */
  const confirmSend = useCallback(async () => {
    if (!staged || accepting) return;
    setAccepting(true);

    // The endpoint rejects these; Composer has no reason to know that.
    const { coach_only, reply_to_id, mentions, idempotency_key, ...rest } = staged;
    // Frozen here, not read again: the selection is allowed to change under us
    // from this point on, and everything about this batch refers to the list as
    // it was when the coach pressed Send.
    const sending = recipients;
    const roomIds = sending.map((r) => r.roomId);

    try {
      const res = await bulkSendMessage(commsPost, {
        ...rest,
        room_ids: roomIds,
        idempotency_key: attemptKeyRef.current,
      });

      const acceptedId = res?.data?.batch_id;
      if (!acceptedId) {
        throw new Error("The server accepted the send but returned no batch id.");
      }
      startBulkSend(acceptedId, roomIds);
      setSentTo(sending);
      setWatchingId(acceptedId);

      setStaged(null);
    } catch (err) {
      setToast({
        id: Date.now(),
        type: "error",
        message: err?.message || "Messages could not be sent.",
      });
    } finally {
      setAccepting(false);
    }
  }, [staged, accepting, recipients]);

  // ── Watching the batch ─────────────────────────────────────────────────────
  // The id comes from the store, not local state, so a send stays visible
  // across this pane unmounting — close the dock mid-send, reopen, and the
  // progress is still there and still moving.
  const batches = useSyncExternalStore(subscribeBulkSends, getBulkSendSnapshot);
  const batchId = watchingId;
  const batch = batchId ? batches[batchId] : undefined;
  const isWatching = Boolean(batch);

  /**
   * Give up waiting when the events dry up.
   *
   * Two ways to lose them: the socket is down, or it reconnected and whatever
   * was published during the gap is gone — the `broadcast` namespace has no
   * Centrifugo history, so nothing replays it. After a genuinely quiet spell
   * the remaining rows settle to `unknown` rather than spinning forever.
   *
   * A RECONNECT IS NOT A VERDICT. It used to settle the batch outright, on the
   * reasoning that the gap's events are unrecoverable — true, but everything
   * the server publishes AFTER the reconnect still arrives, and that branch
   * threw those away too. On a slow media batch it turned a send that was still
   * delivering into one "Sent" and a column of "Check the thread", which is
   * indistinguishable from a send that only reached one person. So a reconnect
   * restarts the window from now: rows that are still coming get counted, and
   * only real silence settles anything.
   */
  const epochRef = useRef(connectionEpoch);
  /** When the socket last came back, so the window can restart from there. */
  const reconnectedAtRef = useRef(0);
  useEffect(() => {
    if (!batch || batch.finishedAt) return undefined;

    if (connectionEpoch !== epochRef.current) {
      epochRef.current = connectionEpoch;
      reconnectedAtRef.current = Date.now();
    }

    // Whichever is more recent: the last thing we heard, or the moment we
    // could start hearing things again.
    const since = Math.max(batch.lastEventAt, reconnectedAtRef.current);
    const timer = setTimeout(
      () => settleBulkSend(batchId),
      Math.max(0, idleTimeoutFor(batch.total) - (Date.now() - since))
    );
    return () => clearTimeout(timer);
  }, [batch, batchId, connectionEpoch]);

  /** Report the outcome once, when the batch settles. */
  const reportedRef = useRef(null);
  useEffect(() => {
    if (!batch?.finishedAt || reportedRef.current === batchId) return;
    reportedRef.current = batchId;

    // Many rooms' last_message previews just changed; nothing else would
    // correct the roster until the provider's 45s tick.
    requestChatResync();

    const failedRooms = new Set(
      Object.entries(batch.byRoom)
        .filter(([, status]) => status === ROOM_FAILED)
        .map(([roomId]) => roomId)
    );
    // Only genuine failures narrow the selection for a retry. An `unknown` row
    // is not resent: the message almost certainly landed, and re-sending it
    // would be a no-op at best and a duplicate-looking mess at worst.
    //
    // The key rotates HERE and nowhere else. Held after a partial failure so a
    // retry is exactly-once per room against `(room_id, idempotency_key)`;
    // rotated after a clean batch so the next message the coach writes is a new
    // message rather than one the server dedupes away as already delivered.
    if (failedRooms.size === 0) attemptKeyRef.current = genKey();
    onSent(shown.filter((r) => failedRooms.has(r.roomId)).map((r) => r.clientId));
  }, [batch, batchId, shown, onSent]);

  /** Forget the batch. Shared by both ways of leaving the progress view. */
  const dropBatch = useCallback(() => {
    if (batchId) clearBulkSend(batchId);
    setWatchingId(null);
    setSentTo(null);
  }, [batchId]);

  /**
   * Stop watching and go back to the selection, cleared.
   *
   * Drops the batch from the store on the way out. Safe even mid-send: the work
   * is the server's now, and a batch nobody is displaying is just a leak.
   */
  const finishWatching = useCallback(() => {
    dropBatch();
    onDone();
  }, [dropBatch, onDone]);

  /**
   * Write the next message to the same people, without leaving.
   *
   * The escape hatch this pane was missing. Every other route out of a finished
   * batch went through the parent, and the parent unmounted the pane before the
   * batch was ever dropped — so the batch outlived its own progress view and
   * the next compose opened straight back into it. This is the button that ends
   * that cycle, and it is also just the thing a coach wants after a send.
   *
   * Does NOT touch `attemptKeyRef`: the report effect has already rotated it if
   * this batch was clean, and deliberately kept it if it was not.
   */
  const composeAnother = useCallback(() => {
    dropBatch();
    setStaged(null);
    // The restore slot is a one-shot for `Edit` on the confirm bar. Left alone,
    // a draft the coach cancelled earlier would be seeded back into the
    // composer AFTER the send it was cancelled from — the pane now outlives the
    // batch, so it has to be emptied by hand.
    setRestore((prev) => ({ text: "", nonce: prev.nonce + 1 }));
  }, [dropBatch]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-app-bg">
      <div className="flex flex-shrink-0 items-start gap-2 border-b border-gray-100 bg-white px-3 py-2.5">
        <button
          type="button"
          onClick={isWatching ? finishWatching : onBack}
          aria-label="Back to selection"
          title="Back to selection"
          className="mt-0.5 rounded-lg p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <Users
              aria-hidden="true"
              className="h-3.5 w-3.5 flex-shrink-0 text-orange-primary"
            />
            <span className="text-xs font-semibold text-gray-900">
              {shown.length} recipient{shown.length === 1 ? "" : "s"}
            </span>
          </span>
          <span
            className="mt-0.5 block truncate text-xs text-gray-400"
            title={isWatching ? undefined : summary}
          >
            {isWatching
              ? `${batch.sent} of ${batch.total} sent${
                  batch.failed > 0 ? ` · ${batch.failed} failed` : ""
                }`
              : summary}
          </span>
        </span>
      </div>

      {/* Coaches will assume either a group chat or individual messages, and the
          wrong assumption is embarrassing in front of a client. Say which. */}
      {!isWatching && (
        <p className="flex flex-shrink-0 items-start gap-2 bg-light-orange px-3.5 py-2 text-xs leading-relaxed text-gray-700">
          <AlertTriangle
            aria-hidden="true"
            className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-orange-primary"
          />
          Each client gets this as a private message in their own chat. They
          will not see each other.
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3">
        {isWatching ? (
          <BulkProgressList
            recipients={shown}
            byRoom={batch.byRoom}
            errors={batch.errors}
          />
        ) : staged ? (
          <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Ready to send
            </p>
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">
              {staged.content || <em className="text-gray-400">No text</em>}
            </p>
            {staged.attachments?.length > 0 && (
              <p className="mt-1.5 text-xs text-gray-400">
                {staged.attachments.length} attachment
                {staged.attachments.length === 1 ? "" : "s"}
              </p>
            )}
          </div>
        ) : (
          <p className="text-center text-xs leading-relaxed text-gray-400">
            Write the message below. You'll get a chance to review it before it
            goes out.
          </p>
        )}
      </div>

      <div className="flex-shrink-0">
        {isWatching ? (
          <div className="flex items-center gap-2 border-t border-gray-100 bg-white px-3 py-2.5">
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700">
              {!batch.finishedAt
                ? connected
                  ? "Delivering…"
                  : "Connection lost — still delivering"
                : batch.failed > 0
                ? `${batch.failed} didn't send`
                : "All sent"}
            </span>
            {/* Only once it is over. Mid-send the work is still the server's,
                and offering a fresh composer over a running batch invites a
                second send nobody is watching. */}
            {batch.finishedAt && (
              <button
                type="button"
                onClick={composeAnother}
                className="rounded-lg bg-orange-primary px-2.5 py-1.5 text-xs font-semibold text-white transition-[filter] hover:brightness-95"
              >
                Send another
              </button>
            )}
            {/* Deliberately never disabled mid-send: the work is server-side
                now, so leaving is safe and the progress keeps updating in the
                store whether or not this pane is mounted. */}
            <button
              type="button"
              onClick={finishWatching}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              {batch.finishedAt ? "Done" : "Hide"}
            </button>
          </div>
        ) : staged ? (
          <div className="flex items-center gap-2 border-t border-gray-100 bg-white px-3 py-2.5">
            <span className="text-xs font-medium text-gray-700">
              Send to {recipients.length} client
              {recipients.length === 1 ? "" : "s"}?
            </span>
            <button
              type="button"
              onClick={cancelStaged}
              disabled={accepting}
              className="ml-auto rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={confirmSend}
              disabled={accepting}
              className="rounded-lg bg-orange-primary px-3 py-1.5 text-xs font-semibold text-white transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {accepting ? "Sending…" : "Send"}
            </button>
          </div>
        ) : (
          <Composer
            modes={["client"]}
            participants={[]}
            onSubmit={handleStage}
            initialText={restore.text}
            initialTextNonce={restore.nonce}
          />
        )}
      </div>

      {toast && (
        <Toaster key={toast.id} message={toast.message} type={toast.type} />
      )}
    </div>
  );
}

export default BulkComposePane;
